// components/admin/DeliveryAgentManagement.tsx - FIXED OPTIONAL FIELD VALIDATION
import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Pagination,
  IconButton,
  Typography,
  Avatar,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  TextField,
  InputAdornment,
  Card,
  Chip,
  Grid,
  Stack,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Delete,
  Edit,
  Add,
  Search,
  ArrowBack,
  Email,
  Phone,
  LocationOn,
  Badge,
  Upload,
  DirectionsCar,
  CardMembership,
  CheckCircle,
  Cancel,
  HourglassEmpty,
  Close,
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';
import {
  useListDeliveryAgentsQuery,
  useAddDeliveryAgentMutation,
  useUpdateDeliveryAgentMutation,
  useDeleteDeliveryAgentMutation,
  useApproveDeliveryAgentMutation,
  DeliveryAgent,
  AddDeliveryAgentRequest,
  UpdateDeliveryAgentRequest,
} from '../../features/admin/deliveryAgent.ts';

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
}

interface FormData {
  first_name: string;
  last_name: string;
  email: string;
  contact: string;
  password: string;
  address: string;
  nationality: string;
  // ── OPTIONAL DOCUMENTS ───────────────────────────────────────────
  id_no: string;
  id_front: File | string | null;
  id_back: File | string | null;
  id_validity?: string | null;
  vehicle_reg_no: string;
  vehicle_reg_front: File | string | null;
  vehicle_reg_back: File | string | null;
  vehicle_reg_validity?: string | null;
  licence_no: string;
  licence_front: File | string | null;
  licence_back: File | string | null;
  licence_validity?: string | null;
  // ── REQUIRED DOCUMENT ────────────────────────────────────────────
  passport_no: string;
  passport_front: File | string | null;
  passport_back: File | string | null;
  passport_validity?: string | null;
  // ── STATUS (admin use) ────────────────────────────────────────────
  licence_status?: number;
  licence_reject_reason?: string;
  id_status?: number;
  id_reject_reason?: string;
  passport_status?: number;
  passport_reject_reason?: string;
  vehicle_reg_status?: number;
  vreg_reject_reason?: string;
}

interface FormErrors {
  [key: string]: string;
}

interface RejectDialogState {
  open: boolean;
  agentId: number | null;
  documentType: 'licence' | 'id' | 'passport' | 'vehicle_reg' | null;
  reason: string;
}

interface ImagePreviewState {
  [key: string]: string;
}

const DeliveryAgentManagement: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [page, setPage] = useState(1);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchStr, setSearchStr] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [showForm, setShowForm] = useState(false);
  const [editingAgent, setEditingAgent] = useState<DeliveryAgent | null>(null);
  const [formData, setFormData] = useState<FormData>({
    first_name: '',
    last_name: '',
    email: '',
    contact: '',
    password: '',
    address: '',
    nationality: '',
    id_no: '',
    id_front: null,
    id_back: null,
    id_validity: null,
    vehicle_reg_no: '',
    vehicle_reg_front: null,
    vehicle_reg_back: null,
    vehicle_reg_validity: null,
    licence_no: '',
    licence_front: null,
    licence_back: null,
    licence_validity: null,
    passport_no: '',
    passport_front: null,
    passport_back: null,
    passport_validity: null,
  });

  const [imagePreviews, setImagePreviews] = useState<ImagePreviewState>({});
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [rejectDialog, setRejectDialog] = useState<RejectDialogState>({
    open: false,
    agentId: null,
    documentType: null,
    reason: '',
  });

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const agentsPerPage = 10;

  const { data: agentsResponse, isLoading, error, isError, refetch } = useListDeliveryAgentsQuery();
  const [addDeliveryAgent] = useAddDeliveryAgentMutation();
  const [updateDeliveryAgent] = useUpdateDeliveryAgentMutation();
  const [deleteDeliveryAgent, { isLoading: isDeleting }] = useDeleteDeliveryAgentMutation();
  const [approveDeliveryAgent] = useApproveDeliveryAgentMutation();

  const deliveryAgents = agentsResponse?.data || [];

  const handleScrollLeft = () => {
    scrollContainerRef.current?.scrollBy({ left: -300, behavior: 'smooth' });
  };
  const handleScrollRight = () => {
    scrollContainerRef.current?.scrollBy({ left: 300, behavior: 'smooth' });
  };

  const getImageUrl = (imagePath: string | null): string => {
    if (!imagePath) return '';
    if (typeof imagePath === 'string' && (imagePath.startsWith('http://') || imagePath.startsWith('https://'))) return imagePath;
    if (typeof imagePath === 'string' && imagePath.startsWith('data:')) return imagePath;
    if (typeof imagePath !== 'string') return '';

    let cleanPath = imagePath.trim();
    if (cleanPath.includes('/')) cleanPath = cleanPath.split('/').pop()!;
    if (cleanPath.includes('\\')) cleanPath = cleanPath.split('\\').pop()!;

    const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://fftestservice.sarsetech.com';
    const base = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE;
    return `${base}/images/${cleanPath}`;
  };

  const getAgentOverallStatus = (agent: DeliveryAgent): number => {
    const hasVehicleReg = !!(agent.vehicle_reg_no && agent.vehicle_reg_no.trim());
    const hasLicence = !!(agent.licence_no && agent.licence_no.trim());
    const hasId = !!(agent.id_no && agent.id_no.trim());

    if (
      agent.licence_status === 2 ||
      agent.id_status === 2 ||
      agent.passport_status === 2 ||
      (agent.vehicle_reg_status === 2 && hasVehicleReg)
    ) return 2;

    const passportApproved = agent.passport_status === 1;
    const licenceApproved = !hasLicence || agent.licence_status === 1;
    const idApproved = !hasId || agent.id_status === 1;
    const vehicleRegApproved = !hasVehicleReg || agent.vehicle_reg_status !== 2; // ✅ pending is fine

    if (passportApproved && licenceApproved && idApproved && vehicleRegApproved) return 1;
    return 0;
  };

  const filteredAgentsByTab = useMemo(() => {
    if (tabValue === 0) return deliveryAgents;
    return deliveryAgents.filter((agent: DeliveryAgent) => {
      const status = getAgentOverallStatus(agent);
      if (tabValue === 1) return status === 1;
      if (tabValue === 2) return status === 0;
      if (tabValue === 3) return status === 2;
      return true;
    });
  }, [deliveryAgents, tabValue]);

  const filteredAgents = useMemo(() => {
    return filteredAgentsByTab.filter((agent: DeliveryAgent) =>
      !searchStr ||
      agent.first_name.toLowerCase().includes(searchStr.toLowerCase()) ||
      agent.last_name.toLowerCase().includes(searchStr.toLowerCase()) ||
      agent.email.toLowerCase().includes(searchStr.toLowerCase()) ||
      agent.contact.includes(searchStr) ||
      agent.id.toString().includes(searchStr)
    );
  }, [filteredAgentsByTab, searchStr]);

  const approvedCount = deliveryAgents.filter((a: DeliveryAgent) => getAgentOverallStatus(a) === 1).length;
  const pendingCount = deliveryAgents.filter((a: DeliveryAgent) => getAgentOverallStatus(a) === 0).length;
  const rejectedCount = deliveryAgents.filter((a: DeliveryAgent) => getAgentOverallStatus(a) === 2).length;

  const totalRecords = filteredAgents.length;
  const totalPages = Math.ceil(totalRecords / agentsPerPage);
  const paginatedAgents = filteredAgents.slice((page - 1) * agentsPerPage, page * agentsPerPage);
  const isEditing = Boolean(editingAgent);

  const handleChangePage = (_: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
    if (scrollContainerRef.current) scrollContainerRef.current.scrollLeft = 0;
  };
  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => { setSearchStr(e.target.value); setPage(1); };
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => { setTabValue(newValue); setPage(1); };

  const resetForm = () => {
    Object.values(imagePreviews).forEach(url => { if (url.startsWith('blob:')) URL.revokeObjectURL(url); });
    setFormData({
      first_name: '', last_name: '', email: '', contact: '', password: '', address: '', nationality: '',
      id_no: '', id_front: null, id_back: null, id_validity: null,
      vehicle_reg_no: '', vehicle_reg_front: null, vehicle_reg_back: null, vehicle_reg_validity: null,
      licence_no: '', licence_front: null, licence_back: null, licence_validity: null,
      passport_no: '', passport_front: null, passport_back: null, passport_validity: null,
    });
    setImagePreviews({});
    setFormErrors({});
    setEditingAgent(null);
  };

  const handleAddAgent = () => { resetForm(); setShowForm(true); };

  const handleEditAgent = (agent: DeliveryAgent) => {
    Object.values(imagePreviews).forEach(url => { if (url.startsWith('blob:')) URL.revokeObjectURL(url); });

    const newFormData: FormData = {
      first_name: agent.first_name || '',
      last_name: agent.last_name || '',
      email: agent.email || '',
      contact: agent.contact || '',
      password: '',
      address: agent.address || '',
      nationality: agent.nationality || '',
      id_no: agent.id_no || '',
      id_front: null, id_back: null,
      id_validity: (agent as any).id_validity ? (agent as any).id_validity.split('T')[0] : null,
      vehicle_reg_no: agent.vehicle_reg_no || '',
      vehicle_reg_front: null, vehicle_reg_back: null,
      vehicle_reg_validity: (agent as any).vehicle_reg_validity &&
        (agent as any).vehicle_reg_validity !== '0000-00-00' ?
        (agent as any).vehicle_reg_validity.split('T')[0] : null,
      licence_no: agent.licence_no || '',
      licence_front: null, licence_back: null,
      licence_validity: (agent as any).licence_validity ? (agent as any).licence_validity.split('T')[0] : null,
      passport_no: agent.passport_no || '',
      passport_front: null, passport_back: null,
      passport_validity: (agent as any).passport_validity ? (agent as any).passport_validity.split('T')[0] : null,
      licence_status: agent.licence_status || 0,
      licence_reject_reason: agent.licence_reject_reason || '',
      id_status: agent.id_status || 0,
      id_reject_reason: agent.id_reject_reason || '',
      passport_status: agent.passport_status || 0,
      passport_reject_reason: agent.passport_reject_reason || '',
      vehicle_reg_status: agent.vehicle_reg_status || 0,
      vreg_reject_reason: agent.vreg_reject_reason || '',
    };

    const newPreviews: ImagePreviewState = {};
    const imageFields = [
      'id_front', 'id_back', 'passport_front', 'passport_back',
      'licence_front', 'licence_back', 'vehicle_reg_front', 'vehicle_reg_back'
    ];
    imageFields.forEach(field => {
      const val = (agent as any)[field];
      if (val && typeof val === 'string') {
        (newFormData as any)[field] = val;
        const previewUrl = getImageUrl(val);
        if (previewUrl) newPreviews[field] = previewUrl;
      }
    });

    setFormData(newFormData);
    setImagePreviews(newPreviews);
    setEditingAgent(agent);
    setShowForm(true);
  };

  const handleBackToList = () => { setShowForm(false); resetForm(); };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, fieldName: string) => {
    const { files } = e.target;
    if (files && files[0]) {
      const file = files[0];
      if (imagePreviews[fieldName]?.startsWith('blob:')) URL.revokeObjectURL(imagePreviews[fieldName]);
      const previewUrl = URL.createObjectURL(file);
      setImagePreviews(prev => ({ ...prev, [fieldName]: previewUrl }));
      setFormData(prev => ({ ...prev, [fieldName]: file }));
      if (formErrors[fieldName]) setFormErrors(prev => ({ ...prev, [fieldName]: '' }));
    }
  };

  const handleRemoveImage = (fieldName: string) => {
    if (imagePreviews[fieldName]?.startsWith('blob:')) URL.revokeObjectURL(imagePreviews[fieldName]);
    setImagePreviews(prev => { const n = { ...prev }; delete n[fieldName]; return n; });
    setFormData(prev => ({ ...prev, [fieldName]: null }));
  };

  // ═══════════════════════════════════════════════════════════════════════
  // ✅ FIXED validateForm — matches actual backend required fields exactly
  //
  // Backend REQUIRED (adddeliveryagent):
  //   first_name, last_name, email, contact, address, password, nationality
  //   licence_front (file), id_front (file)
  //   passport_validity, id_validity, licence_validity (dates)
  //
  // Backend OPTIONAL (no server-side enforcement):
  //   licence_no, licence_back, id_no, id_back
  //   passport_no, passport_front, passport_back
  //   vehicle_reg_no, vehicle_reg_front, vehicle_reg_back, vehicle_reg_validity
  //   safety_name, safety_contact
  //
  // NOTE: The backend checks `licence_front` and `id_front` as file presence.
  //       If you do NOT send them, the backend returns 400 "Required fields missing".
  //       All other document files/numbers are optional.
  // ═══════════════════════════════════════════════════════════════════════
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // ── 1. Personal info — always required ────────────────────────────
    if (!formData.first_name.trim()) newErrors.first_name = 'First name is required';
    if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required';

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.contact.trim()) newErrors.contact = 'Contact is required';
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.nationality.trim()) newErrors.nationality = 'Nationality is required';

    // Password: required only when creating (not editing)
    if (!isEditing && !formData.password.trim()) {
      newErrors.password = 'Password is required';
    }

    // ── 2. Backend-required document fields (new agent only) ──────────
    //    Backend checks: licence_front, id_front, passport_validity,
    //                    id_validity, licence_validity
    if (!isEditing) {
      // licence_front is required by backend
      if (!formData.licence_front && !imagePreviews.licence_front) {
        newErrors.licence_front = 'Licence front image is required';
      }
      // id_front is required by backend
      if (!formData.id_front && !imagePreviews.id_front) {
        newErrors.id_front = 'Emirates ID front image is required';
      }
      // passport_validity is required by backend
      if (!formData.passport_validity) {
        newErrors.passport_validity = 'Passport expiry date is required';
      }
      // id_validity is required by backend
      if (!formData.id_validity) {
        newErrors.id_validity = 'Emirates ID expiry date is required';
      }
      // licence_validity is required by backend
      if (!formData.licence_validity) {
        newErrors.licence_validity = 'Licence expiry date is required';
      }
    }

    // ── 3. Passport: if passport_no provided → front & back required ──
    //    (Backend rule: passport_no && (!passport_front || !passport_back) → 400)
    if (formData.passport_no.trim()) {
      if (!formData.passport_front && !imagePreviews.passport_front) {
        newErrors.passport_front = 'Passport front image is required when passport number is provided';
      }
      if (!formData.passport_back && !imagePreviews.passport_back) {
        newErrors.passport_back = 'Passport back image is required when passport number is provided';
      }
    }

    // ── 4. Date format validation (for any date field that has a value) ─
    const dateFields = ['id_validity', 'passport_validity', 'licence_validity', 'vehicle_reg_validity'];
    dateFields.forEach(field => {
      const v = (formData as any)[field];
      if (v && !/^\d{4}-\d{2}-\d{2}$/.test(v)) {
        newErrors[field] = 'Invalid date format (YYYY-MM-DD)';
      }
    });

    // ── 5. All other documents are fully optional ─────────────────────
    //    licence_no, licence_back, id_no, id_back,
    //    vehicle_reg_no, vehicle_reg_front, vehicle_reg_back,
    //    vehicle_reg_validity  ← NO validation required

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsFormSubmitting(true);

    try {
      let result;
      if (isEditing && editingAgent) {
        const updateData: any = {
          id: editingAgent.id,
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          contact: formData.contact,
          address: formData.address,
          nationality: formData.nationality,
          id_no: formData.id_no || null,
          licence_no: formData.licence_no || null,
          passport_no: formData.passport_no || null,
          vehicle_reg_no: formData.vehicle_reg_no || null,
          licence_status: formData.licence_status ?? 0,
          licence_reject_reason: formData.licence_reject_reason || '',
          id_status: formData.id_status ?? 0,
          id_reject_reason: formData.id_reject_reason || '',
          passport_status: formData.passport_status ?? 0,
          passport_reject_reason: formData.passport_reject_reason || '',
          vehicle_reg_status: formData.vehicle_reg_status ?? 0,
          vreg_reject_reason: formData.vreg_reject_reason || '',
          id_validity: formData.id_validity || null,
          passport_validity: formData.passport_validity || null,
          licence_validity: formData.licence_validity || null,
          vehicle_reg_validity: formData.vehicle_reg_validity || null,
        } as UpdateDeliveryAgentRequest;

        if (formData.password) updateData.password = formData.password;

        // Only attach File objects (skip string paths — those are existing server files)
        const fileFields = [
          'id_front', 'id_back', 'passport_front', 'passport_back',
          'licence_front', 'licence_back', 'vehicle_reg_front', 'vehicle_reg_back'
        ];
        fileFields.forEach(field => {
          const value = (formData as any)[field];
          if (value instanceof File) updateData[field] = value;
        });

        result = await updateDeliveryAgent(updateData).unwrap();
      } else {
        const addData: any = {
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          contact: formData.contact,
          password: formData.password,
          address: formData.address,
          nationality: formData.nationality,
          id_no: formData.id_no || null,
          licence_no: formData.licence_no || null,
          passport_no: formData.passport_no || null,
          vehicle_reg_no: formData.vehicle_reg_no || null,
          licence_status: formData.licence_status ?? 0,
          licence_reject_reason: formData.licence_reject_reason || '',
          id_status: formData.id_status ?? 0,
          id_reject_reason: formData.id_reject_reason || '',
          passport_status: formData.passport_status ?? 0,
          passport_reject_reason: formData.passport_reject_reason || '',
          vehicle_reg_status: formData.vehicle_reg_status ?? 0,
          vreg_reject_reason: formData.vreg_reject_reason || '',
          id_validity: formData.id_validity || null,
          passport_validity: formData.passport_validity || null,
          licence_validity: formData.licence_validity || null,
          vehicle_reg_validity: formData.vehicle_reg_validity || null,
        } as AddDeliveryAgentRequest;

        const fileFields = [
          'id_front', 'id_back', 'passport_front', 'passport_back',
          'licence_front', 'licence_back', 'vehicle_reg_front', 'vehicle_reg_back'
        ];
        fileFields.forEach(field => {
          const value = (formData as any)[field];
          if (value instanceof File) addData[field] = value;
        });

        result = await addDeliveryAgent(addData).unwrap();
      }

      setSnackbar({
        open: true,
        message: result.message || `Delivery agent ${isEditing ? 'updated' : 'added'} successfully!`,
        severity: 'success'
      });
      handleBackToList();
      refetch();
    } catch (error: any) {
      console.error('Submit error:', error);
      setSnackbar({
        open: true,
        message: error?.data?.message || error.message || `Error ${isEditing ? 'updating' : 'adding'} delivery agent.`,
        severity: 'error'
      });
    } finally {
      setIsFormSubmitting(false);
    }
  };

  const handleDelete = async (agent: DeliveryAgent) => {
    if (window.confirm(`Are you sure you want to delete ${agent.first_name} ${agent.last_name}?`)) {
      try {
        const result = await deleteDeliveryAgent({ id: agent.id }).unwrap();
        setSnackbar({ open: true, message: result.message || 'Delivery agent deleted successfully!', severity: 'success' });
        refetch();
      } catch (err: any) {
        setSnackbar({ open: true, message: err?.data?.message || err?.message || 'Error deleting delivery agent.', severity: 'error' });
      }
    }
  };

  const handleApprove = async (agentId: number, documentType: 'licence' | 'id' | 'passport' | 'vehicle_reg' | null) => {
    try {
      const approveData: any = { id: agentId };
      if (documentType) {
        approveData[`${documentType}_status`] = 1;
      } else {
        approveData.licence_status = 1;
        approveData.id_status = 1;
        approveData.passport_status = 1;
        approveData.vehicle_reg_status = 1;
      }
      const result = await approveDeliveryAgent(approveData).unwrap();
      setSnackbar({ open: true, message: result.message || 'Approved successfully!', severity: 'success' });
      refetch();
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.data?.message || err?.message || 'Error approving document.', severity: 'error' });
    }
  };

  const handleOpenRejectDialog = (agentId: number, documentType: 'licence' | 'id' | 'passport' | 'vehicle_reg') => {
    setRejectDialog({ open: true, agentId, documentType, reason: '' });
  };
  const handleCloseRejectDialog = () => {
    setRejectDialog({ open: false, agentId: null, documentType: null, reason: '' });
  };

  const handleReject = async () => {
    if (!rejectDialog.reason.trim()) {
      setSnackbar({ open: true, message: 'Please provide a rejection reason', severity: 'error' });
      return;
    }
    try {
      const rejectData: any = {
        id: rejectDialog.agentId,
        [`${rejectDialog.documentType}_status`]: 2,
        [`${rejectDialog.documentType}_reject_reason`]: rejectDialog.reason,
      };
      const result = await approveDeliveryAgent(rejectData).unwrap();
      setSnackbar({ open: true, message: result.message || 'Document rejected', severity: 'info' });
      handleCloseRejectDialog();
      refetch();
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.data?.message || err?.message || 'Error rejecting document.', severity: 'error' });
    }
  };

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  const renderDocumentStatus = (
    status?: number,
    rejectReason?: string,
    agentId?: number,
    documentType?: 'licence' | 'id' | 'passport' | 'vehicle_reg',
    expiryDate?: string | null,
    isOptional: boolean = false,
    isProvided: boolean = true
  ) => {
    if (!isProvided && isOptional) {
      return <Chip label="Not Provided" size="small" sx={{ bgcolor: '#f0f0f0', color: '#6d7175', fontWeight: 500, fontSize: '11px' }} />;
    }
    if (!isProvided && !isOptional) {
      return <Chip label="Required" size="small" sx={{ bgcolor: '#fff3cd', color: '#856404', fontWeight: 600, fontSize: '11px' }} />;
    }
    if (status === 1) {
      return (
        <Box>
          <Chip label="Approved" size="small" icon={<CheckCircle sx={{ fontSize: 16 }} />} sx={{ bgcolor: '#d1f4e0', color: '#00783e', fontWeight: 600, fontSize: '11px' }} />
          {expiryDate && <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#6d7175' }}>Expires: {expiryDate}</Typography>}
        </Box>
      );
    } else if (status === 2) {
      return (
        <Tooltip title={rejectReason || 'Rejected'} arrow>
          <Box>
            <Chip label="Rejected" size="small" icon={<Cancel sx={{ fontSize: 16 }} />} sx={{ bgcolor: '#ffd6d6', color: '#c41c1c', fontWeight: 600, fontSize: '11px', cursor: 'pointer' }} />
            {expiryDate && <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#6d7175' }}>Expires: {expiryDate}</Typography>}
          </Box>
        </Tooltip>
      );
    } else {
      return (
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <Chip label="Pending" size="small" icon={<HourglassEmpty sx={{ fontSize: 16 }} />} sx={{ bgcolor: '#fef7cd', color: '#7c6e0a', fontWeight: 600, fontSize: '11px' }} />
          {agentId && documentType && (
            <Box sx={{ display: 'flex', gap: 0.25 }}>
              <Tooltip title="Approve" arrow>
                <IconButton size="small" onClick={() => handleApprove(agentId, documentType)} sx={{ color: '#00783e', p: 0.5, '&:hover': { bgcolor: '#d1f4e0' } }}>
                  <CheckCircle sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Reject" arrow>
                <IconButton size="small" onClick={() => handleOpenRejectDialog(agentId, documentType)} sx={{ color: '#c41c1c', p: 0.5, '&:hover': { bgcolor: '#ffd6d6' } }}>
                  <Cancel sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          {expiryDate && <Typography variant="caption" sx={{ ml: 0.5, color: '#6d7175' }}>Expires: {expiryDate}</Typography>}
        </Box>
      );
    }
  };

  const renderOverallStatus = (agent: DeliveryAgent) => {
    const status = getAgentOverallStatus(agent);
    if (status === 1) return <Chip label="Approved" size="small" sx={{ bgcolor: '#d1f4e0', color: '#00783e', fontWeight: 600 }} />;
    if (status === 2) return <Chip label="Rejected" size="small" sx={{ bgcolor: '#ffd6d6', color: '#c41c1c', fontWeight: 600 }} />;
    return (
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        <Chip label="Pending" size="small" sx={{ bgcolor: '#fef7cd', color: '#7c6e0a', fontWeight: 600 }} />
        <Tooltip title="Approve All Documents" arrow>
          <IconButton size="small" onClick={() => handleApprove(agent.id, null)} sx={{ color: '#00783e', p: 0.5, '&:hover': { bgcolor: '#d1f4e0' } }}>
            <CheckCircle sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  // ── File upload field renderer ──────────────────────────────────────────
  const renderFileUploadField = (fieldName: string, label: string, required: boolean = false) => {
    const previewUrl = imagePreviews[fieldName];
    const err = formErrors[fieldName];
    const hasImage = !!previewUrl;

    return (
      <Box>
        <Box
          sx={{
            border: `2px dashed ${err ? '#d32f2f' : hasImage ? '#c9cccf' : '#c9cccf'}`,
            borderRadius: '12px',
            p: 2,
            textAlign: 'center',
            cursor: 'pointer',
            bgcolor: '#fafbfb',
            transition: 'all 0.2s',
            minHeight: '180px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': { borderColor: err ? '#d32f2f' : '#202223', bgcolor: '#f6f6f7' }
          }}
          onClick={() => document.getElementById(`file-upload-${fieldName}`)?.click()}
        >
          {hasImage ? (
            <Box sx={{ position: 'relative', width: '100%' }}>
              <Box
                component="img"
                src={previewUrl}
                alt={label}
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                sx={{
                  width: '100%',
                  height: '150px',
                  objectFit: 'contain',
                  borderRadius: '8px',
                  display: 'block',
                }}
              />
              {previewUrl.startsWith('blob:') && (
                <Typography variant="caption" display="block" sx={{ color: '#6d7175', mt: 0.5 }}>
                  New image selected
                </Typography>
              )}
              <IconButton
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  bgcolor: 'white',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                  '&:hover': { bgcolor: '#f5f5f5' },
                  width: 28,
                  height: 28,
                }}
                size="small"
                onClick={(e) => { e.stopPropagation(); handleRemoveImage(fieldName); }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>
          ) : (
            <Box>
              <Box sx={{
                width: 56, height: 56, borderRadius: '8px', bgcolor: 'white',
                border: '1px solid #e1e3e5', display: 'flex', alignItems: 'center',
                justifyContent: 'center', mx: 'auto', mb: 2
              }}>
                <Upload sx={{ fontSize: 24, color: '#6d7175' }} />
              </Box>
              <Button variant="outlined" sx={{
                textTransform: 'none',
                borderColor: err ? '#d32f2f' : '#c9cccf',
                color: '#202223', fontWeight: 500, borderRadius: '8px',
                '&:hover': { borderColor: '#202223', bgcolor: 'transparent' }
              }}>
                Upload {label}
              </Button>
              <Typography variant="caption" display="block" sx={{ color: err ? '#d32f2f' : '#6d7175', mt: 1 }}>
                {required ? 'Required *' : 'Optional'}
              </Typography>
            </Box>
          )}
        </Box>
        <input
          id={`file-upload-${fieldName}`}
          type="file"
          accept="image/*,.pdf"
          onChange={(e) => handleFileChange(e, fieldName)}
          style={{ display: 'none' }}
          disabled={isFormSubmitting}
        />
        {err && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block', ml: 1.5 }}>
            {err}
          </Typography>
        )}
      </Box>
    );
  };

  useEffect(() => {
    return () => {
      Object.values(imagePreviews).forEach(url => { if (url.startsWith('blob:')) URL.revokeObjectURL(url); });
    };
  }, [imagePreviews]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>
        <Drawer variant="temporary" open={mobileOpen} onClose={handleDrawerToggle} ModalProps={{ keepMounted: true }} sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: 250 } }}>
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>

        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />
          <Box sx={{ flexGrow: 1, overflow: 'auto', backgroundColor: '#f1f2f4' }}>
            {!showForm ? (
              // ── LIST VIEW ──────────────────────────────────────────────────
              <Box sx={{ maxWidth: 1800, mx: 'auto', p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>Delivery Agents</Typography>
                  <Button variant="contained" startIcon={<Add />} onClick={handleAddAgent} sx={{ bgcolor: '#202223', color: 'white', textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: 2.5, '&:hover': { bgcolor: '#303030' } }}>
                    Add agent
                  </Button>
                </Box>

                <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', overflow: 'hidden' }}>
                  <Box sx={{ borderBottom: '1px solid #e1e3e5' }}>
                    <Tabs value={tabValue} onChange={handleTabChange} sx={{ px: 2, '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, fontSize: '14px', minHeight: 48, color: '#6d7175' }, '& .Mui-selected': { color: '#202223', fontWeight: 600 }, '& .MuiTabs-indicator': { backgroundColor: '#202223', height: 3 } }}>
                      <Tab label={`All (${deliveryAgents.length})`} />
                      <Tab label={`Approved (${approvedCount})`} />
                      <Tab label={`Pending (${pendingCount})`} />
                      <Tab label={`Rejected (${rejectedCount})`} />
                    </Tabs>
                  </Box>

                  <Box sx={{ p: 2, borderBottom: '1px solid #e1e3e5', display: 'flex', gap: 1 }}>
                    <TextField size="small" placeholder="Search agents" value={searchStr} onChange={handleSearch} fullWidth sx={{ maxWidth: 400, '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: 'white' } }}
                      InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" sx={{ color: '#6d7175' }} /></InputAdornment> }} />
                  </Box>

                  {isLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, bgcolor: 'white' }}>
                      <CircularProgress size={32} sx={{ color: '#008060' }} />
                    </Box>
                  )}
                  {isError && !isLoading && (
                    <Box sx={{ p: 3, bgcolor: 'white' }}>
                      <Alert severity="error">Failed to load delivery agents. Please try again.</Alert>
                    </Box>
                  )}
                  {!isLoading && !isError && paginatedAgents.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white' }}>
                      <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>No delivery agents found</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {searchStr ? `No results for "${searchStr}"` : 'Get started by adding your first delivery agent'}
                      </Typography>
                    </Box>
                  )}

                  {!isLoading && paginatedAgents.length > 0 && (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1, borderBottom: '1px solid #e1e3e5', bgcolor: '#fafbfb' }}>
                        <IconButton size="small" onClick={handleScrollLeft} sx={{ color: '#6d7175', border: '1px solid #e1e3e5', borderRadius: '4px', width: 28, height: 28, '&:hover': { bgcolor: '#e1e3e5', color: '#202223' } }}><ChevronLeft fontSize="small" /></IconButton>
                        <Box sx={{ flex: 1, height: '4px', mx: 1, bgcolor: '#e1e3e5', borderRadius: '2px', overflow: 'hidden' }}>
                          <Box sx={{ height: '100%', width: '30%', bgcolor: '#6d7175', borderRadius: '2px', opacity: 0.4 }} />
                        </Box>
                        <IconButton size="small" onClick={handleScrollRight} sx={{ color: '#6d7175', border: '1px solid #e1e3e5', borderRadius: '4px', width: 28, height: 28, '&:hover': { bgcolor: '#e1e3e5', color: '#202223' } }}><ChevronRight fontSize="small" /></IconButton>
                      </Box>

                      <Box ref={scrollContainerRef} sx={{ bgcolor: 'white', overflowX: 'auto', '&::-webkit-scrollbar': { height: '8px' }, '&::-webkit-scrollbar-track': { background: '#f1f2f4', borderRadius: '4px' }, '&::-webkit-scrollbar-thumb': { background: '#c1c4c8', borderRadius: '4px', '&:hover': { background: '#a1a4a8' } } }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '50px 80px 200px 200px 140px 140px 140px 140px 140px 150px 100px', gap: 2, px: 2, py: 1.5, borderBottom: '1px solid #e1e3e5', bgcolor: '#fafbfb', minWidth: '1600px' }}>
                          {['No', 'ID', 'Name', 'Email', 'License', 'ID Doc', 'Passport', 'Vehicle Reg', 'Overall Status', 'Actions'].map(h => (
                            <Typography key={h} variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', textAlign: h === 'Actions' ? 'right' : 'left' }}>{h}</Typography>
                          ))}
                        </Box>

                        {paginatedAgents.map((agent: DeliveryAgent, index: number) => (
                          <Box key={agent.id} sx={{ display: 'grid', gridTemplateColumns: '50px 80px 200px 200px 140px 140px 140px 140px 140px 150px 100px', gap: 2, px: 2, py: 2, alignItems: 'center', borderBottom: index < paginatedAgents.length - 1 ? '1px solid #e1e3e5' : 'none', '&:hover': { bgcolor: '#f9fafb' }, transition: 'background-color 0.15s ease', minWidth: '1600px' }}>
                            <Typography variant="body2" sx={{ color: '#6d7175' }}>{(page - 1) * agentsPerPage + index + 1}</Typography>
                            <Chip label={`#${agent.id}`} size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: '11px' }} />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar sx={{ width: 40, height: 40, bgcolor: '#008060', fontSize: '14px', fontWeight: 600 }}>
                                {((agent.first_name || '')[0] || '') + ((agent.last_name || '')[0] || '')}
                              </Avatar>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: '#202223' }}>{agent.first_name} {agent.last_name}</Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>{agent.email}</Typography>
                            <Box>{renderDocumentStatus(agent.licence_status, agent.licence_reject_reason, agent.id, 'licence', (agent as any).licence_validity ? (agent as any).licence_validity.split('T')[0] : null, true, !!agent.licence_no)}</Box>
                            <Box>{renderDocumentStatus(agent.id_status, agent.id_reject_reason, agent.id, 'id', (agent as any).id_validity ? (agent as any).id_validity.split('T')[0] : null, true, !!agent.id_no)}</Box>
                            <Box>{renderDocumentStatus(agent.passport_status, agent.passport_reject_reason, agent.id, 'passport', (agent as any).passport_validity ? (agent as any).passport_validity.split('T')[0] : null, false, true)}</Box>
                            <Box>{renderDocumentStatus(agent.vehicle_reg_status, agent.vreg_reject_reason, agent.id, 'vehicle_reg', (agent as any).vehicle_reg_validity ? (agent as any).vehicle_reg_validity.split('T')[0] : null, true, !!agent.vehicle_reg_no)}</Box>
                            <Box>{renderOverallStatus(agent)}</Box>
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                              <IconButton size="small" onClick={() => handleEditAgent(agent)} sx={{ color: '#6d7175', '&:hover': { bgcolor: '#f1f2f4' } }}><Edit fontSize="small" /></IconButton>
                              <IconButton size="small" onClick={() => handleDelete(agent)} disabled={isDeleting} sx={{ color: '#6d7175', '&:hover': { bgcolor: '#f1f2f4' } }}><Delete fontSize="small" /></IconButton>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </>
                  )}
                </Card>

                {totalPages > 1 && !isLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination count={totalPages} page={page} onChange={handleChangePage} sx={{ '& .MuiPaginationItem-root': { borderRadius: '8px', fontWeight: 500 }, '& .Mui-selected': { bgcolor: '#202223 !important', color: 'white' } }} />
                  </Box>
                )}
              </Box>
            ) : (
              // ── FORM VIEW ──────────────────────────────────────────────────
              <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
                <Box sx={{ mb: 3 }}>
                  <Button startIcon={<ArrowBack />} onClick={handleBackToList} sx={{ color: '#202223', textTransform: 'none', fontWeight: 500, mb: 2, '&:hover': { bgcolor: 'transparent' } }}>
                    Delivery Agents
                  </Button>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                    {isEditing ? 'Edit delivery agent' : 'Add delivery agent'}
                  </Typography>
                </Box>

                {/* Required fields notice */}
                <Box sx={{ mb: 3, p: 2, bgcolor: '#e3f2fd', borderRadius: '8px', border: '1px solid #90caf9' }}>
                  <Typography variant="body2" sx={{ color: '#0d47a1', fontWeight: 500 }}>
                    ℹ️ <strong>Required fields:</strong> Personal info, Emirates ID front image + expiry, Licence front image + expiry, Passport expiry.
                    &nbsp;All other document fields (numbers, back images, vehicle reg) are <strong>optional</strong>.
                  </Typography>
                </Box>

                <form onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                      <Stack spacing={2}>

                        {/* Personal Information */}
                        <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                          <Box sx={{ p: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>Personal information</Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6}>
                                <TextField name="first_name" label="First name *" value={formData.first_name} onChange={handleInputChange} error={!!formErrors.first_name} helperText={formErrors.first_name} fullWidth required disabled={isFormSubmitting} />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField name="last_name" label="Last name *" value={formData.last_name} onChange={handleInputChange} error={!!formErrors.last_name} helperText={formErrors.last_name} fullWidth required disabled={isFormSubmitting} />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField name="email" label="Email *" type="email" value={formData.email} onChange={handleInputChange} error={!!formErrors.email} helperText={formErrors.email} fullWidth required disabled={isFormSubmitting}
                                  InputProps={{ startAdornment: <InputAdornment position="start"><Email fontSize="small" sx={{ color: '#6d7175' }} /></InputAdornment> }} />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField name="contact" label="Contact *" value={formData.contact} onChange={handleInputChange} error={!!formErrors.contact} helperText={formErrors.contact} fullWidth required disabled={isFormSubmitting}
                                  InputProps={{ startAdornment: <InputAdornment position="start"><Phone fontSize="small" sx={{ color: '#6d7175' }} /></InputAdornment> }} />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField name="nationality" label="Nationality *" value={formData.nationality} onChange={handleInputChange} error={!!formErrors.nationality} helperText={formErrors.nationality} fullWidth required disabled={isFormSubmitting} />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField name="password" label={isEditing ? 'Password (leave blank to keep current)' : 'Password *'} type="password" value={formData.password} onChange={handleInputChange} error={!!formErrors.password} helperText={formErrors.password} fullWidth required={!isEditing} disabled={isFormSubmitting} />
                              </Grid>
                              <Grid item xs={12}>
                                <TextField name="address" label="Address *" value={formData.address} onChange={handleInputChange} error={!!formErrors.address} helperText={formErrors.address} fullWidth required multiline rows={3} disabled={isFormSubmitting}
                                  InputProps={{ startAdornment: <InputAdornment position="start"><LocationOn fontSize="small" sx={{ color: '#6d7175' }} /></InputAdornment> }} />
                              </Grid>
                            </Grid>
                          </Box>
                        </Card>

                        {/* Emirates ID */}
                        {/* Emirates ID */}
                        <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: `1px solid ${!isEditing ? '#f59e0b' : '#e1e3e5'}` }}>
                          <Box sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#202223' }}>Emirates ID</Typography>
                              {!isEditing && <Chip label="Front image + Expiry required" size="small" sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 600, fontSize: '11px' }} />}
                            </Box>
                            <Typography variant="caption" sx={{ color: '#6d7175', mb: 2, display: 'block', fontStyle: 'italic' }}>
                              ID number and back image are optional. Front image and expiry date are required.
                            </Typography>

                            {/* Row 1: text inputs */}
                            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                              <Box sx={{ flex: 1 }}>
                                <TextField name="id_no" label="Emirates ID number (optional)" value={formData.id_no} onChange={handleInputChange} fullWidth disabled={isFormSubmitting} placeholder="Optional" />
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                <TextField name="id_validity" label={`Emirates ID expiry${!isEditing ? ' *' : ''}`} type="date" value={formData.id_validity || ''} onChange={handleInputChange}
                                  InputLabelProps={{ shrink: true }} fullWidth disabled={isFormSubmitting}
                                  error={!!formErrors.id_validity} helperText={formErrors.id_validity || (!isEditing ? 'Required' : 'Optional when editing')} required={!isEditing} />
                              </Box>
                            </Box>

                            {/* Row 2: uploads */}
                            <Box sx={{ display: 'flex', gap: 2 }}>
                              <Box sx={{ flex: 1 }}>
                                {renderFileUploadField('id_front', `ID Front${!isEditing ? ' *' : ''}`, !isEditing)}
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                {renderFileUploadField('id_back', 'ID Back (optional)', false)}
                              </Box>
                            </Box>
                          </Box>
                        </Card>

                        {/* Driver's Licence */}
                        <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: `1px solid ${!isEditing ? '#f59e0b' : '#e1e3e5'}` }}>
                          <Box sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#202223' }}>Driver's Licence</Typography>
                              {!isEditing && <Chip label="Front image + Expiry required" size="small" sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 600, fontSize: '11px' }} />}
                            </Box>
                            <Typography variant="caption" sx={{ color: '#6d7175', mb: 2, display: 'block', fontStyle: 'italic' }}>
                              Licence number and back image are optional. Front image and expiry date are required.
                            </Typography>

                            {/* Row 1: text inputs */}
                            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                              <Box sx={{ flex: 1 }}>
                                <TextField name="licence_no" label="Licence number (optional)" value={formData.licence_no} onChange={handleInputChange} fullWidth disabled={isFormSubmitting} placeholder="Optional"
                                  InputProps={{ startAdornment: <InputAdornment position="start"><CardMembership fontSize="small" sx={{ color: '#6d7175' }} /></InputAdornment> }} />
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                <TextField name="licence_validity" label={`Licence expiry${!isEditing ? ' *' : ''}`} type="date" value={formData.licence_validity || ''} onChange={handleInputChange}
                                  InputLabelProps={{ shrink: true }} fullWidth disabled={isFormSubmitting}
                                  error={!!formErrors.licence_validity} helperText={formErrors.licence_validity || (!isEditing ? 'Required' : 'Optional when editing')} required={!isEditing} />
                              </Box>
                            </Box>

                            {/* Row 2: uploads */}
                            <Box sx={{ display: 'flex', gap: 2 }}>
                              <Box sx={{ flex: 1 }}>
                                {renderFileUploadField('licence_front', `Licence Front${!isEditing ? ' *' : ''}`, !isEditing)}
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                {renderFileUploadField('licence_back', 'Licence Back (optional)', false)}
                              </Box>
                            </Box>
                          </Box>
                        </Card>

                        {/* Passport */}
                        <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: `1px solid ${!isEditing ? '#f59e0b' : '#e1e3e5'}` }}>
                          <Box sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#202223' }}>Passport</Typography>
                              {!isEditing && <Chip label="Expiry required" size="small" sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 600, fontSize: '11px' }} />}
                            </Box>
                            <Typography variant="caption" sx={{ color: '#6d7175', mb: 2, display: 'block', fontStyle: 'italic' }}>
                              Passport number, front and back images are optional — unless passport number is provided (then images are required).
                            </Typography>

                            {/* Row 1: text inputs */}
                            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                              <Box sx={{ flex: 1 }}>
                                <TextField name="passport_no" label="Passport number (optional)" value={formData.passport_no} onChange={handleInputChange} error={!!formErrors.passport_no} helperText={formErrors.passport_no} fullWidth disabled={isFormSubmitting} placeholder="Optional"
                                  InputProps={{ startAdornment: <InputAdornment position="start"><Badge fontSize="small" sx={{ color: '#6d7175' }} /></InputAdornment> }} />
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                <TextField name="passport_validity" label={`Passport expiry${!isEditing ? ' *' : ''}`} type="date" value={formData.passport_validity || ''} onChange={handleInputChange}
                                  InputLabelProps={{ shrink: true }} fullWidth disabled={isFormSubmitting}
                                  error={!!formErrors.passport_validity} helperText={formErrors.passport_validity || (!isEditing ? 'Required' : 'Optional when editing')} required={!isEditing} />
                              </Box>
                            </Box>

                            {/* Row 2: uploads */}
                            <Box sx={{ display: 'flex', gap: 2 }}>
                              <Box sx={{ flex: 1 }}>
                                {renderFileUploadField('passport_front', `Passport Front${formData.passport_no ? ' *' : ' (optional)'}`, !!formData.passport_no)}
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                {renderFileUploadField('passport_back', `Passport Back${formData.passport_no ? ' *' : ' (optional)'}`, !!formData.passport_no)}
                              </Box>
                            </Box>
                          </Box>
                        </Card>
                        {/* Vehicle Registration */}
                        <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                          <Box sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#202223' }}>Vehicle Registration</Typography>
                              <Chip label="Fully optional" size="small" sx={{ bgcolor: '#f0f9ff', color: '#0369a1', fontWeight: 600, fontSize: '11px' }} />
                            </Box>
                            <Typography variant="caption" sx={{ color: '#6d7175', mb: 2, display: 'block', fontStyle: 'italic' }}>
                              All vehicle registration fields are completely optional — leave empty if not applicable.
                            </Typography>

                            {/* Row 1: text inputs */}
                            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                              <Box sx={{ flex: 1 }}>
                                <TextField name="vehicle_reg_no" label="Vehicle reg number (optional)" value={formData.vehicle_reg_no} onChange={handleInputChange} fullWidth disabled={isFormSubmitting} placeholder="Optional"
                                  InputProps={{ startAdornment: <InputAdornment position="start"><DirectionsCar fontSize="small" sx={{ color: '#6d7175' }} /></InputAdornment> }} />
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                <TextField name="vehicle_reg_validity" label="Vehicle reg expiry (optional)" type="date" value={formData.vehicle_reg_validity || ''} onChange={handleInputChange}
                                  InputLabelProps={{ shrink: true }} fullWidth disabled={isFormSubmitting}
                                  error={!!formErrors.vehicle_reg_validity} helperText={formErrors.vehicle_reg_validity || 'Optional'} />
                              </Box>
                            </Box>

                            {/* Row 2: uploads */}
                            <Box sx={{ display: 'flex', gap: 2 }}>
                              <Box sx={{ flex: 1 }}>
                                {renderFileUploadField('vehicle_reg_front', 'Vehicle Reg Front (optional)', false)}
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                {renderFileUploadField('vehicle_reg_back', 'Vehicle Reg Back (optional)', false)}
                              </Box>
                            </Box>
                          </Box>
                        </Card>

                      </Stack>
                    </Grid>

                    {/* Sidebar — Document Approval Status */}
                    <Grid item xs={12} md={4}>
                      <Stack spacing={2}>
                        <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', position: 'sticky', top: 16 }}>
                          <Box sx={{ p: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>Document Approval Status</Typography>
                            {[
                              { key: 'licence', label: 'License Status', statusKey: 'licence_status', reasonKey: 'licence_reject_reason', validityKey: 'licence_validity', disabledWhen: false },
                              { key: 'id', label: 'Emirates ID Status', statusKey: 'id_status', reasonKey: 'id_reject_reason', validityKey: 'id_validity', disabledWhen: false },
                              { key: 'passport', label: 'Passport Status', statusKey: 'passport_status', reasonKey: 'passport_reject_reason', validityKey: 'passport_validity', disabledWhen: false },
                              { key: 'vehicle_reg', label: 'Vehicle Reg Status', statusKey: 'vehicle_reg_status', reasonKey: 'vreg_reject_reason', validityKey: 'vehicle_reg_validity', disabledWhen: !formData.vehicle_reg_no },
                            ].map(({ key, label, statusKey, reasonKey, validityKey, disabledWhen }) => (
                              <Box key={key} sx={{ mb: 2 }}>
                                <Typography variant="caption" sx={{ color: '#6d7175', mb: 0.5, display: 'block' }}>{label}</Typography>
                                <Select size="small" fullWidth value={String((formData as any)[statusKey] ?? 0)}
                                  onChange={(e) => setFormData(prev => ({ ...prev, [statusKey]: Number(e.target.value) }))}
                                  disabled={isFormSubmitting || disabledWhen}
                                  sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: '#c9cccf' }, borderRadius: '8px' }}>
                                  <MenuItem value={0}>{disabledWhen ? 'Not Provided' : 'Pending'}</MenuItem>
                                  <MenuItem value={1}>Approved</MenuItem>
                                  <MenuItem value={2}>Rejected</MenuItem>
                                </Select>
                                {(formData as any)[statusKey] === 2 && (
                                  <TextField size="small" fullWidth placeholder="Rejection reason" value={(formData as any)[reasonKey] || ''}
                                    onChange={(e) => setFormData(prev => ({ ...prev, [reasonKey]: e.target.value }))} disabled={isFormSubmitting} sx={{ mt: 1 }} />
                                )}
                                {(formData as any)[validityKey] && (
                                  <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#6d7175' }}>
                                    Expires: {(formData as any)[validityKey]}
                                  </Typography>
                                )}
                              </Box>
                            ))}
                            <Box sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: '8px', border: '1px solid #90caf9' }}>
                              <Typography variant="caption" sx={{ color: '#0d47a1', fontWeight: 500 }}>
                                ℹ️ Set approval status for each document. Vehicle Registration shows "Not Provided" if left empty.
                              </Typography>
                            </Box>
                          </Box>
                        </Card>
                      </Stack>
                    </Grid>
                  </Grid>

                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3, pt: 3, borderTop: '1px solid #e1e3e5' }}>
                    <Button onClick={handleBackToList} disabled={isFormSubmitting} sx={{ textTransform: 'none', fontWeight: 600, color: '#eeeeee', bgcolor: '#f44336', borderRadius: '8px', px: 2.5 }}>Close</Button>
                    <Button onClick={handleBackToList} disabled={isFormSubmitting} sx={{ textTransform: 'none', fontWeight: 600, color: '#eeeeee', bgcolor: '#F78E12', borderRadius: '8px', px: 2.5 }}>Discard</Button>
                    <Button type="submit" variant="contained" disabled={isFormSubmitting} sx={{ bgcolor: '#008060', textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: 2.5, '&:hover': { bgcolor: '#006e52' } }}>
                      {isFormSubmitting ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={20} color="inherit" /> Saving...
                        </Box>
                      ) : 'Save'}
                    </Button>
                  </Box>
                </form>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog.open} onClose={handleCloseRejectDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, color: '#202223' }}>Reject {rejectDialog.documentType?.replace('_', ' ')}</DialogTitle>
        <DialogContent>
          <TextField autoFocus margin="dense" label="Rejection Reason" fullWidth multiline rows={4} value={rejectDialog.reason} onChange={(e) => setRejectDialog(prev => ({ ...prev, reason: e.target.value }))} placeholder="Please provide a reason for rejection..." sx={{ mt: 2 }} />
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={handleCloseRejectDialog} sx={{ textTransform: 'none', fontWeight: 600, color: '#6d7175' }}>Cancel</Button>
          <Button onClick={handleReject} variant="contained" sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#c41c1c', '&:hover': { bgcolor: '#a01616' } }}>Reject Document</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default DeliveryAgentManagement;